/**
 * Sync lock — multi-tab deduplication using the Web Locks API.
 *
 * Ensures only one browser tab runs the sync engine at a time.
 * Falls back to running without locking when `navigator.locks` is
 * unavailable (e.g., in non-secure contexts or older browsers).
 */

/**
 * Name of the cross-tab sync single-flight lock. Exported so the cross-tab
 * reconciliation probe (`isSyncRunningInAnotherTab`) can recognise it in
 * `navigator.locks.query()`.
 */
export const SYNC_LOCK_NAME = "ow2-sync-lock";
const LOCK_NAME = SYNC_LOCK_NAME;

/**
 * Execute `fn` while holding a Web Lock, SKIPPING (not queuing) if another tab
 * or hook instance already holds it.
 *
 * P0-c: `{ ifAvailable: true }` grants `null` instead of queuing when the lock
 * is held — so rapid/overlapping sync triggers (across tabs, or a remount race
 * the per-instance `isSyncingRef` doesn't cover) collapse to ONE run instead of
 * running back-to-back duplicates. The previous queue-and-wait behavior was a
 * storm amplifier: a trigger that slipped past the in-tab guard would wait, then
 * run a redundant full sync. This Web Lock is now the authoritative single-flight
 * (cross-tab + cross-instance); `useSync`'s `isSyncingRef` stays an in-tab
 * fast-path that never causes a duplicate.
 *
 * Returns whether `fn` actually ran (false = skipped because another holder was
 * active). When `navigator.locks` is unavailable, `fn` runs directly.
 */
export async function withSyncLock(fn: () => Promise<void>): Promise<boolean> {
  if (!navigator.locks) {
    // Fallback: no Web Locks API available — just run the function.
    await fn();
    return true;
  }

  let ran = false;
  await navigator.locks.request(
    LOCK_NAME,
    { ifAvailable: true } as LockOptions,
    async (lock) => {
      if (!lock) {
        // Another tab/instance holds the lock — SKIP, don't queue.
        return;
      }
      ran = true;
      await fn();
    },
  );
  return ran;
}

// --- Cross-tab reconciliation probe (#958) -----------------------------------
//
// The chat-list reconciliation gate (`syncScheduler.ts`) is MODULE-LEVEL =
// per-tab. But this sync lock is CROSS-TAB and SKIPS (`ifAvailable:true`) when
// another tab holds it. So with 2+ tabs, a lock-skipped tab drops its own gate
// and its `decryptionRetryOrchestrator` fires legacy `getMessage` refetches
// against the SAME per-user server budget the lock-holder tab is paginating —
// re-opening the cold-start starvation incident (#927 only closed the same-tab
// case). See issue #958.
//
// Fix without a NEW lock (no change to the `ifAvailable` skip semantics above):
// the reconciling tab ALREADY holds this sync lock for its whole `runSync`, so a
// deferrable background consumer in another tab can simply OBSERVE it via the
// read-only, never-contending `navigator.locks.query()` and defer while it's
// held/pending elsewhere. The browser auto-releases a Web Lock when the holding
// tab dies/navigates, so a dead holder can NEVER permanently block another tab
// (no heartbeat, no lease, no stale-state cleanup) — the key correctness win.
//
// `query()` cannot tell us our OWN agent's `Client.id` directly, so we learn it
// by holding a unique per-tab SENTINEL lock for the page lifetime and reading
// its `clientId` back from `query()`. A Web Locks `clientId` is per-agent and
// identical across ALL of this tab's lock entries, so the sentinel's clientId IS
// our sync-lock clientId — letting us exclude our own sync lock from the probe
// (otherwise a tab would defer on itself = a single-tab latency regression).

// This tab's Web Locks `clientId`, learned from a held sentinel lock (see below).
// `null` until derived. Derivation is RETRIED on every probe until it succeeds —
// a one-shot cache here would, on a derivation miss, permanently treat this tab's
// OWN sync lock as another tab's and self-defer for the whole wait cap (a
// single-tab regression). `selfClientIdPromise` single-flights an in-progress
// derivation; it is cleared on failure so the next probe re-derives.
let selfClientId: string | null = null;
let selfClientIdPromise: Promise<void> | null = null;
// Whether the page-lifetime sentinel lock has been requested (at most once), and
// its unique name (read back from query() to learn our clientId).
let sentinelRequested = false;
let selfSentinelName: string | null = null;

/** Wall-clock budget for a single self-clientId derivation attempt. */
const SELF_CLIENT_ID_DERIVE_BUDGET_MS = 1_000;
const SELF_CLIENT_ID_POLL_MS = 25;

/** Best-effort unique id for the sentinel lock name. */
function uniqueId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  // Insecure context / no WebCrypto — degrade gracefully (consistent with the
  // navigator.locks fallback). Uniqueness here only needs to avoid a same-tab
  // sentinel-name collision, not be cryptographically strong.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface LockInfoLike {
  name?: string;
  clientId?: string;
}
interface LockManagerLike {
  request?: (...args: unknown[]) => unknown;
  query?: () => Promise<{ held?: LockInfoLike[]; pending?: LockInfoLike[] }>;
}

function getLocks(): LockManagerLike | undefined {
  return (navigator as unknown as { locks?: LockManagerLike }).locks;
}

/**
 * Learn this tab's Web Locks `clientId` (lazy). Holds a unique sentinel lock for
 * the page lifetime and reads its `clientId` back from `query()` — every entry
 * from THIS agent shares one `clientId`, so the sentinel's id is also our sync
 * lock's id, letting us exclude our own lock from the cross-tab probe.
 *
 * RETRY semantics (vs a one-shot cache): if a derivation attempt finishes
 * without learning the id (the grant hasn't landed within the budget, or
 * `query()` threw), `selfClientIdPromise` is cleared so the NEXT probe tries
 * again. The sentinel `request()` is issued at most once and its (rare)
 * rejection is swallowed. Bounded by wall-clock, not a fixed poll count, so
 * event-loop congestion (the cold-start storm) can't make the budget meaningless.
 */
function ensureSelfClientId(
  budgetMs = SELF_CLIENT_ID_DERIVE_BUDGET_MS,
): Promise<void> {
  if (selfClientId !== null) {
    return Promise.resolve();
  }
  if (selfClientIdPromise !== null) {
    // A derivation is already in flight — but it may have been started by an
    // EARLIER caller with a larger budget. Awaiting it verbatim would make THIS
    // caller wait up to that larger budget (≤ SELF_CLIENT_ID_DERIVE_BUDGET_MS),
    // overshooting its own smaller `budgetMs` and breaking the single-deadline
    // invariant `awaitReconciliationIdleCrossTab` relies on. Race the in-flight
    // derivation against this caller's own budget: the background derivation
    // keeps running (it still sets `selfClientId` + clears the marker in its
    // `finally`, so the next probe benefits), but this caller returns within its
    // own budget. On the timeout branch `selfClientId` may still be null → the
    // probe conservatively defers one poll and re-derives, exactly as on a
    // first-probe miss.
    if (budgetMs <= 0) {
      return Promise.resolve();
    }
    const cap = Math.max(
      0,
      Math.min(SELF_CLIENT_ID_DERIVE_BUDGET_MS, budgetMs),
    );
    return Promise.race([
      selfClientIdPromise,
      new Promise<void>((r) => setTimeout(r, cap)),
    ]);
  }
  const locks = getLocks();
  if (!locks?.request || !locks?.query) {
    return Promise.resolve();
  }

  if (!sentinelRequested) {
    sentinelRequested = true;
    const sentinelName = `ow2-self-probe-${uniqueId()}`;
    // Hold the sentinel for the page lifetime: the callback returns a promise
    // that never resolves, so the lock stays held until the tab is closed (when
    // the browser auto-releases it). Not awaited — the held promise must not
    // block. A rejection means ACQUISITION failed (AbortError/SecurityError/lock
    // API blip): swallow it so it can't surface as an unhandled rejection, AND
    // reset the once-guard so a later probe re-requests a fresh sentinel — else
    // `selfSentinelName` would point at a lock that never appears in `query()`
    // and `selfClientId` would stay null for the tab's lifetime (treating our
    // OWN sync lock as foreign). Only clear if no id was learned meanwhile.
    selfSentinelName = sentinelName;
    const onRequestFailed = () => {
      // ACQUISITION failed: reset the once-guard so a later probe re-requests a
      // fresh sentinel — else `selfSentinelName` would point at a lock that never
      // appears in `query()` and `selfClientId` would stay null for the tab's
      // lifetime (treating our OWN sync lock as foreign). Only clear if no id was
      // learned meanwhile.
      if (selfClientId === null && selfSentinelName === sentinelName) {
        sentinelRequested = false;
        selfSentinelName = null;
      }
    };
    try {
      // Hold the sentinel for the page lifetime: the callback returns a promise
      // that never resolves, so the lock stays held until the tab is closed
      // (browser auto-release). Not awaited — the held promise must not block. A
      // rejection (AbortError/SecurityError/lock API blip) is swallowed so it
      // can't surface as an unhandled rejection. The `try/catch` ALSO covers a
      // SYNCHRONOUS throw from `request()` (a malformed-name/impl edge) — without
      // it, a sync throw would leave `sentinelRequested` poisoned forever.
      void Promise.resolve(
        (
          locks.request as (
            name: string,
            cb: () => Promise<void>,
          ) => Promise<unknown>
        )(sentinelName, () => new Promise<void>(() => {})),
      ).catch(onRequestFailed);
    } catch {
      onRequestFailed();
    }
  }

  selfClientIdPromise = (async () => {
    // Cap at the smaller of the derive budget and the caller's remaining budget,
    // so this can never push the caller's total deferral past its own deadline.
    const deadline =
      Date.now() +
      Math.max(0, Math.min(SELF_CLIENT_ID_DERIVE_BUDGET_MS, budgetMs));
    while (selfClientId === null && Date.now() < deadline) {
      try {
        const { held = [] } = await locks.query!();
        const mine = held.find((l) => l.name === selfSentinelName);
        if (mine?.clientId) {
          selfClientId = mine.clientId;
          return;
        }
      } catch {
        // query() blipped — stop this attempt; the next probe retries.
        break;
      }
      // Yield so the pending grant can land between polls.
      await new Promise<void>((r) => setTimeout(r, SELF_CLIENT_ID_POLL_MS));
    }
  })().finally(() => {
    // Clear the in-flight marker. If we learned the id, the `selfClientId !==
    // null` short-circuit at the top makes future calls cheap; if not, the next
    // probe re-derives instead of being stuck on a resolved-but-empty promise.
    selfClientIdPromise = null;
  });
  return selfClientIdPromise;
}

/**
 * True iff ANOTHER client (tab/worker) currently HOLDS or is PENDING on the sync
 * lock — i.e. a sync/reconciliation is running elsewhere. Read-only, cross-tab,
 * never contends (uses `navigator.locks.query()`, not `request()`).
 *
 * Returns `false` when `navigator.locks`/`query` is unavailable (jsdom, insecure
 * context) so callers degrade to the per-tab gate only — exactly today's
 * behavior. Excludes THIS tab's own lock via `clientId` so a single tab never
 * defers on itself. If our own `clientId` isn't known yet (only on the very
 * first probe, before the sentinel grant lands — derivation is RETRIED each
 * probe so this never sticks), we conservatively treat every holder as "other"
 * (defer) rather than risk wrongly proceeding past a real cross-tab holder; the
 * next probe re-derives and corrects within the same poll loop.
 *
 * `deriveBudgetMs` caps the (one-time) self-id derivation so a caller polling
 * under a wall-clock deadline can pass its remaining budget and guarantee this
 * probe never overshoots that deadline.
 */
export async function isSyncRunningInAnotherTab(
  deriveBudgetMs?: number,
): Promise<boolean> {
  const locks = getLocks();
  if (!locks?.query) {
    return false;
  }
  await ensureSelfClientId(deriveBudgetMs);
  let state: { held?: LockInfoLike[]; pending?: LockInfoLike[] };
  try {
    state = await locks.query();
  } catch {
    return false;
  }
  const entries = [...(state.held ?? []), ...(state.pending ?? [])];
  return entries.some(
    (l) => l.name === SYNC_LOCK_NAME && l.clientId !== selfClientId,
  );
}

/** Test-only: reset the self-clientId derivation state so cases can re-derive it. */
export function _resetSelfClientId(value: string | null = null): void {
  selfClientId = value;
  selfClientIdPromise = null;
  sentinelRequested = false;
  selfSentinelName = null;
}
